import { QueryOptions } from '@apollo/client';

import { gql } from '@/__generated__/gateway';
import {
  ContextInput,
  CountryConfigsQuery,
  CountryConfigsQueryVariables,
  CustomerAccessTokenQuery,
  CustomerAccessTokenQueryVariables,
  GetNotificationsInput,
  NotificationsQuery,
  NotificationsQueryVariables,
  PatternBySkuPdfQuery,
  PatternBySkuPdfQueryVariables,
  PatternBySkuQuery,
  PatternBySkuQueryVariables,
  PatternLocalesQuery,
  PatternLocalesQueryVariables,
  PostCountersQuery,
  PostCountersQueryVariables,
  StarterDataQuery,
  StarterDataQueryVariables
} from '@/__generated__/gateway/graphql';

export const customerAccessTokenQuery = gql(`
  query CustomerAccessToken($storeName: String!) {
    shopify {
      exchangeToken(store_name: $storeName) {
        customerAccessToken {
          accessToken
          expiresAt
        }
      }
    }
  }
`);

export const queryCustomerAccessToken = (
  storeName: string
): QueryOptions<
  CustomerAccessTokenQueryVariables,
  CustomerAccessTokenQuery
> => ({
  query: customerAccessTokenQuery,
  fetchPolicy: 'no-cache',
  variables: { storeName }
});

export const queryStarterData = (
  input: GetNotificationsInput,
  alias: string,
  userId: string
): QueryOptions<StarterDataQueryVariables, StarterDataQuery> => ({
  query: gql(`
    query StarterData(
      $input: GetNotificationsInput!
      $alias: String!
      $userId: ID!
    ) {
      notification {
        notifications(input: $input) {
          unread
          unseen
        }
      }
      toc {
        status(alias: $alias) {
          success
        }
      }
      community {
        profile(user_id: $userId) {
          followers_count
          following_count
          user {
            id
            bio
            username
            display_name
            country
            avatar_url
          }
        }
      }
    }
  `),
  fetchPolicy: 'no-cache',
  variables: { input, alias, userId }
});

export const notificationsQuery = gql(`
  query Notifications($input: GetNotificationsInput!) {
    notification {
      notifications(input: $input) {
        unread
        unseen
        total
        cursor
        notifications {
          id
          message
          read_at
          created_at
          actor {
            username
            avatar_url
          }
          sourceable_type
          sourceable_id
          subject {
            ... on Post {
              id
            }
            ... on Comment {
              id
              post_id
            }
          }
        }
      }
    }
  }
`);

export const queryNotifications = (
  input: GetNotificationsInput
): QueryOptions<NotificationsQueryVariables, NotificationsQuery> => ({
  query: notificationsQuery,
  fetchPolicy: 'no-cache',
  variables: { input }
});

export const queryCountryConfigs = (): QueryOptions<
  CountryConfigsQueryVariables,
  CountryConfigsQuery
> => ({
  query: gql(`
    query countryConfigs {
      countryConfigs {
        countryCode
        shopifyConfig {
          storefront {
            endpoint
            name
            publicAccessToken
          }
          storeEndpoint
        }
      }
    }
  `),
  fetchPolicy: 'no-cache',
  variables: {}
});

export const customerContentQuery = gql(`
  query customerContent ($language: String!, $limit: Int = 10, $cursor: String = null) {
    customerContent(language: $language, limit: $limit, cursor: $cursor) {
      content {
        ... on Pattern {
          id
          sku
          url
          createdAt
          updatedAt
        }
      }
      cursor
    }
  }
`);

export const queryTabCounters = (variables: {
  galleryContext: ContextInput;
  qaContext: ContextInput;
}): QueryOptions<PostCountersQueryVariables, PostCountersQuery> => ({
  query: gql(`
    query PostCounters($galleryContext: ContextInput, $qaContext: ContextInput) {
      community {
        gallery: posts(context: $galleryContext) {
          total
        }
        qa: posts(context: $qaContext) {
          total
        }
      }
    }
  `),
  variables
});

export const loyaltyPointsQuery = gql(`
  query loyalty {
    loyalty {
      points {
        cursor
        limit
        points {
          amount
          created_at
          type
          description
        }
      }
    }
  }
`);

export const loyaltyBalanceQuery = gql(`
  query loyaltyBalance {
    loyalty {
      user {
        balance
      }
    }
  }
`);

// TAF-3343: the account page's order history. Replaces the Storefront query
// of the same name, which needed a Shopify customer access token in the
// browser. Nothing here identifies the customer — the gateway takes that from
// the pass on the request, so `storeName` only says which store to read.
export const customerOrderHistoryQuery = gql(`
  query CustomerOrderHistory($storeName: String!, $limit: Int, $cursor: String) {
    orders {
      history(store_name: $storeName, limit: $limit, cursor: $cursor) {
        orders {
          id
          orderNumber
          processedAt
          fulfillmentStatus
          statusUrl
          totalPrice {
            amount
            currencyCode
          }
          lineItemCount
          hasMoreLineItems
          requiresShipping
        }
        hasNextPage
        cursor
        limit
        windowDays
      }
    }
  }
`);

export const queryPatternBySku = (
  sku: string,
  language = 'en',
  urlVersion = '2024-07-05',
  contactInfo?: {
    contactUUID?: string;
    countryCode?: string;
  }
): QueryOptions<PatternBySkuQueryVariables, PatternBySkuQuery> => ({
  query: gql(`
    query PatternBySku(
      $sku: String!
      $urlVersion: String
      $language: String
      $contactUUID: String
      $countryCode: String
    ) {
      patternBySku(
        sku: $sku
        urlVersion: $urlVersion
        language: $language
        contactUUID: $contactUUID
        countryCode: $countryCode
      ) {
        url
        sku
        id
        createdAt
        updatedAt
      }
    }
  `),
  fetchPolicy: 'network-only',
  variables: {
    sku: sku,
    language: language,
    urlVersion: urlVersion,
    contactUUID: contactInfo?.contactUUID,
    countryCode: contactInfo?.countryCode
  }
});

export const patternLocalesQuery = gql(`
  query PatternLocales($id: ID, $sku: String) {
    patternLocales(id: $id, sku: $sku) {
      locales
    }
  }
`);

export const queryPatternLocales = (variables: {
  id?: string;
  sku?: string;
}): QueryOptions<PatternLocalesQueryVariables, PatternLocalesQuery> => ({
  query: patternLocalesQuery,
  fetchPolicy: 'network-only',
  variables
});

export const patternBySkuPdfQuery = gql(`
  query PatternBySkuPdf($sku: String!, $language: String, $contactUUID: String) {
    patternBySku(sku: $sku, language: $language, contactUUID: $contactUUID) {
      id
      url
    }
  }
`);

export const queryPatternBySkuPdf = (
  sku: string,
  language?: string,
  contactUUID?: string
): QueryOptions<PatternBySkuPdfQueryVariables, PatternBySkuPdfQuery> => ({
  query: patternBySkuPdfQuery,
  fetchPolicy: 'network-only',
  variables: { sku, language, contactUUID }
});

export const countryBingoIdQuery = gql(`
  query BingoId($countryConfigInput: CountryConfigInput) {
    countryConfigs(countryConfigInput: $countryConfigInput) {
      bingoId
    }
  }
`);

export const crafterProjectsQuery = gql(`
  query CrafterProjects($cursor: String, $limit: Int, $states: [ProjectState!]) {
    crafter {
      userProjects(cursor: $cursor, limit: $limit,  states: $states) {
        projects {
          ...ListProject
        }
        limit
        cursor
      }
    }
  }
`);

export const crafterProjectQuery = gql(`
  query CrafterProject(
      $projectId: ID!,
      $activitiesSortOrder: SortOrder,
      $activitiesLimit: Int,
      $activitiesCursor: String,
      $notesLimit: Int,
      $notesCursor: String
    ) {
    crafter {
      userProject(id: $projectId) {
        ...SingleProject
      }
    }
  }
`);

export const crafterYarnQuery = gql(`
  query CrafterYarn($id: ID!) {
    crafter {
      userYarn(id: $id) {
        ...FullYarn
      }
    }
  }
`);

// TODO: Minimize the data requested here.
export const crafterYarnsQuery = gql(`
  query CrafterYarns($filters: YarnFilterInput, $sortBy: YarnSortBy, $sortOrder: SortOrder, $cursor: String, $limit: Int) {
    crafter {
      userYarns(filters: $filters, sortBy: $sortBy, sortOrder: $sortOrder, cursor: $cursor, limit: $limit) {
        cursor
        filters {
          ... on FilterRange {
            label
            max
            min
            type
          }
          ... on FilterItems {
            items {
              label
              results
            }
            label
            type
          }
        }
        limit
        yarns {
          ...FullYarn
        }
      }
    }
  }
`);

export const crafterYarnSuggestionsQuery = gql(`
  query CrafterYarnSuggestions($projectId: ID!, $limit: Int, $sortBy: YarnSortBy, $sortOrder: SortOrder, $suggestedCursor: String, $otherCursor: String) {
    crafter {
      userYarnSuggestions(projectId: $projectId, limit: $limit, sortBy: $sortBy, sortOrder: $sortOrder, suggestedCursor: $suggestedCursor, otherCursor: $otherCursor) {
        suggested {
          limit
          cursor
          filters {
            ... on FilterRange {
              label
              type
              min
              max
            }
            ... on FilterItems {
              label
              type
              items {
                label
                results
              }
            }
          }
          yarns {
            ...FullYarn
          }
        }
        other {
          limit
          cursor
          filters {
            ... on FilterRange {
              label
              type
              min
              max
            }
            ... on FilterItems {
              label
              type
              items {
                label
                results
              }
            }
          }
          yarns {
            ...FullYarn
          }
        }
      }
    }
  }
`);

export const crafterPatternsQuery = gql(`
  query CrafterPatterns($filters: PatternFilterInput, $sortBy: PatternSortBy, $sortOrder: SortOrder, $cursor: String, $limit: Int) {
    crafter {
      userPatterns(filters: $filters, sortBy: $sortBy, sortOrder: $sortOrder, cursor: $cursor, limit: $limit) {
        cursor
        limit
        filters {
          ... on FilterItems {
            items {
              label
              results
            }
            label
            type
          }
        }
        patterns {
          id
          name
          craft_type
          image {
            url
            alt
          }
          projects {
            id
            name
            state
          }
          created_at
          updated_at
        }
      }
    }
  }
`);

export const publicCrafterProjectQuery = gql(`
  query PublicCrafterProject(
      $shortId: ID!,
      $activitiesSortOrder: SortOrder,
      $activitiesLimit: Int,
      $activitiesCursor: String,
    ) {
    crafter {
      publicProject(shortId: $shortId) {
        short_id
        name
        state
        craft_type
        image {
          url
        }
        recipient
        started_at
        ends_at
        published_at
        projectActivities(limit: $activitiesLimit, cursor: $activitiesCursor, sortOrder: $activitiesSortOrder) {
          activities {
            type
            details {
              ... on ProjectStateChangeDetails {
                from
                to
              }
              ... on ProjectVisibilityChangeDetails {
                is_public
                published_at
              }
            }
            note {
              content
              images {
                url
              }
              rows
              created_at
              updated_at
            }
            created_at
            updated_at
          }
          limit
          cursor
        }
        pattern {
          name
          craft_type
          image {
            url
            alt
          }
        }
      }
    }
  }
`);

export const crafterMultiWishlistQuery = gql(`
  query CrafterMultiWishlist(
  $sortBy: WishlistSortBy,
  $sortDir: SortOrder,
  $cursor: String,
  $limit: Int,
  $itemsCursor2: String,
  $itemsLimit2: Int) {
  crafter {
    wishlists(sortBy: $sortBy, sortDir: $sortDir, cursor: $cursor,  limit: $limit) {
      cursor
      limit
      wishlists {
        created_at
        description
        id
        is_default
        is_public
        items(cursor: $itemsCursor2, limit: $itemsLimit2) {
          cursor
          items {
            created_at
            id
            product_group_identifier
            sku
          }
          limit
}
        name
        public_id
        updated_at
      }
    }
  }
}
`);

export const crafterSpecificWishlistQuery = gql(`
  query CrafterSpecificWishlist($wishlistId: ID!, $cursor: String, $limit: Int) {
  crafter {
    wishlist(id: $wishlistId) {
      created_at
      description
      id
      is_default
      is_public
      items(cursor: $cursor, limit: $limit) {
        cursor
        items {
          created_at
          id
          product_group_identifier
          sku
        }
        limit
      }
      name
      public_id
      updated_at
    }
  }
}
`);

export const crafterWishlistNamesQuery = gql(`
  query CrafterWishlistNames {
    crafter {
      wishlists {
        wishlists {
          id
          created_at
          public_id
          name
          updated_at
          is_public
          is_default
        }
      }
    }
  }
`);

export const publicCrafterWishlistQuery = gql(`
  query PublicCrafterWishlist($id: ID!, $cursor: String, $limit: Int, $itemsCursor: String, $itemsLimit: Int) {
    crafter {
      publicWishlist(id: $id, cursor: $cursor, limit: $limit) {
        wishlist {
          id
          name
          description
          items(cursor: $itemsCursor, limit: $itemsLimit) {
            cursor
            limit
            items {
              product_group_identifier
              sku
            }
          }
          created_at
          updated_at
        }
        cursor
        limit
      }
    }
  }
`);
